import { Table, Skeleton, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../../providers/AuthProvider';
import { KpiItem, KpiResponse } from './types';

type ValueFormat = 'currency' | 'percent' | 'number';

interface KpiRow {
  key: string;
  label: string;
  target: number | null;
  achievement: number | null;
  format: ValueFormat;
}

interface KpiTableProps {
  kpiData: KpiResponse | null;
  loading: boolean;
  error: string | null;
}

const skeletonRows: KpiRow[] = Array.from({ length: 4 }, (_, index) => ({
  key: `skeleton-${index}`,
  label: '',
  target: 0,
  achievement: 0,
  format: 'number',
}));

const detectValueFormat = (item: KpiItem): ValueFormat => {
  const identifier = `${item.key} ${item.label}`.toLowerCase();

  if (identifier.includes('rate') || identifier.includes('percent')) {
    return 'percent';
  }

  if (
    identifier.includes('value') ||
    identifier.includes('revenue') ||
    identifier.includes('amount') ||
    identifier.includes('deal')
  ) {
    return 'currency';
  }

  return 'number';
};

const formatNumber = (value: number) => {
  const hasFraction = Math.abs(value % 1) > 0;

  return value.toLocaleString(undefined, {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const formatCellValue = (value: number | null, format: ValueFormat, currencySymbol: string) => {
  if (value === null || value === undefined) {
    return '-';
  }

  switch (format) {
    case 'currency':
      return `${currencySymbol}${formatNumber(value)}`;
    case 'percent':
      return `${value.toFixed(2)}%`;
    default:
      return formatNumber(value);
  }
};

export const KpiTable = ({ kpiData, loading, error }: KpiTableProps) => {
  const { userInfo } = useAuth();
  const currencySymbol = userInfo?.company_base_currency_symbol || '$';

  const tableData: KpiRow[] = loading
    ? skeletonRows
    : (kpiData?.data || []).map((item) => ({
        key: item.key || item.kpi_id.toString() || item.label,
        label: item.label,
        target: item.target,
        achievement: item.achievement,
        format: detectValueFormat(item),
      }));

  const columns: ColumnsType<KpiRow> = [
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 100 }} /> : 'KPI',
      dataIndex: 'label',
      key: 'label',
      render: (value: string) => (loading ? <Skeleton.Input active size="small" /> : value),
    },
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : 'Target',
      dataIndex: 'target',
      key: 'target',
      render: (_: number | null, record: KpiRow) =>
        loading ? (
          <Skeleton.Input active size="small" />
        ) : (
          <span>{formatCellValue(record.target, record.format, currencySymbol)}</span>
        ),
    },
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 110 }} /> : 'Achievement',
      dataIndex: 'achievement',
      key: 'achievement',
      render: (_: number | null, record: KpiRow) =>
        loading ? (
          <Skeleton.Input active size="small" />
        ) : (
          <span>{formatCellValue(record.achievement, record.format, currencySymbol)}</span>
        ),
    },
  ];

  return (
    <div className="text-left">
      {error && <Alert message={error} type="error" showIcon className="mb-2" />}
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        size="small"
        loading={false}
        rowKey="key"
        locale={{ emptyText: loading ? <Skeleton active paragraph={false} /> : 'No KPI data available' }}
      />
    </div>
  );
};

export default KpiTable;
